import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FiChevronRight, FiMenu, FiX, FiTarget } from 'react-icons/fi';
import type { Agent } from '../types';
import SkyToggle from './ui/sky-toggle';
import { useAppStore } from '../store/appStore';
import { useWorkspaces } from '../hooks/useWorkspaces';
import { useAgents } from '../hooks/useAgents';
import { useAuth } from '../hooks/useAuth';
import { useCan } from '../hooks/usePermissions';
import { useToast } from '../contexts/ToastContext';
import TrialLimitBanner from './TrialLimitBanner';
import {
  IconGradientDefs,
  IconGrid,
  IconWand,
  IconSparkle,
  IconAutomation,
  IconTicket,
  IconConversations,
  IconSettings,
  // HIDDEN: IconSecurity,
  // HIDDEN: IconBilling,
  // HIDDEN: IconTools,
  IconLogs,
  IconProfile,
  IconLogout,
  IconWebChat,
  IconVoice,
} from './Icons';

type SidebarSection =
  | 'dashboard'
  | 'workspaces'
  | 'agents'
  | 'knowledge'
  | 'channels'
  | 'voiceAgent'
  | 'conversations'
  | 'settings'
  | 'security'
  | 'users'
  | 'tools'
  | 'billing'
  | 'support'
  | 'logs'
  | 'escalations'
  | 'leads';

interface WorkspaceLayoutProps {
  activeSection: SidebarSection;
  children: React.ReactNode;
  pageTitle?: string;
  pageDescription?: string;
  headerActions?: React.ReactNode;
}

const ProjectSidebarLayout: React.FC<WorkspaceLayoutProps> = ({
  activeSection,
  children,
  pageTitle,
  pageDescription,
  headerActions,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, agentId } = useParams<{ projectId: string; agentId?: string }>();
  const isDashboard = location.pathname.startsWith('/dashboard');

  const { workspaces, activeWorkspaceId } = useWorkspaces();
  const { agentsByWorkspace, user } = useAppStore();
  const { logout } = useAuth();
  const { showToast } = useToast();

  // Nav gating. Each flag mirrors the backend @Roles() on the screen behind it,
  // so a role only sees entries whose requests would actually succeed.
  const canManageAgents = useCan('agents.manage');
  const canManageActions = useCan('actions.manage');
  const canManageChannels = useCan('channels.manage');
  const canUseAutomations = useCan('automations.access');
  const canViewLogs = useCan('logs.access');
  // Screen scoping rather than capability: LEAD_MANAGER and MEMBER are
  // lead-desk roles, so the agent builder, voice agent, workspace settings,
  // Users and Support Tickets come out of their nav entirely, leaving Leads,
  // Conversations, and the dashboard for MEMBER (see usePermissions.ts).
  // Conversations stays for them on purpose: it's where the lead desk reads
  // what a lead actually said, and the backend never gated those reads.
  const canViewAgents = useCan('agents.view');
  const canViewConversations = useCan('conversations.view');
  const canUseVoiceAgent = useCan('voice.access');
  const canViewWorkspaceSettings = useCan('workspaceSettings.access');
  const canViewUsers = useCan('users.view');
  const canViewEscalations = useCan('escalations.access');
  // LEAD_MANAGER alone: its surface is Leads, so even the dashboard goes.
  const canViewDashboard = useCan('dashboard.view');
  // CUSTOMER (demo sign-up) is scoped to Channels, Voice Agent and
  // Conversations, so Leads comes out of its nav too.
  const canViewLeads = useCan('leads.view');

  // Every entry under Orchestrate is gated, so the heading would otherwise be
  // left standing on its own for a role that holds none of them.
  const showOrchestrate =
    canViewAgents || canManageChannels || canManageActions || canUseVoiceAgent || canUseAutomations;
  // Same for Monitor: both of its entries are gated. The lead-desk roles hold
  // Conversations but not workspace Settings, so for them the heading stands
  // over a single entry rather than an empty section.
  const showMonitor = canViewConversations || canViewWorkspaceSettings;
  // And Administration: CUSTOMER holds none of its entries.
  const showAdministration = canViewUsers || canViewEscalations || canViewLeads || canViewLogs;
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close mobile sidebar drawer on route change
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Determine which workspace to load agents for
  const workspaceIdForAgents = projectId && projectId !== 'undefined' && projectId !== 'null'
    ? projectId
    : activeWorkspaceId || undefined;

  // Load agents for the current workspace so sidebar always has up-to-date data
  useAgents(workspaceIdForAgents);

  const agents: Agent[] = useMemo(
    () => (workspaceIdForAgents && agentsByWorkspace[workspaceIdForAgents]) || [],
    [agentsByWorkspace, workspaceIdForAgents],
  );
  const hasAgents = agents.length > 0;


  const currentAgent: Agent | undefined =
    (agentId && agents.find((a) => a.id === agentId)) || agents[0];

  const goToAgents = () => {
    if (!projectId || projectId === 'undefined' || projectId === 'null') {
      if (activeWorkspaceId) {
        navigate(`/workspaces/${activeWorkspaceId}/agents`);
      } else if (workspaces.length > 0) {
        navigate(`/workspaces/${workspaces[0].id}/agents`);
      } else {
        navigate('/dashboard');
      }
      return;
    }
    navigate(`/workspaces/${projectId}/agents`);
  };

  const explainDisabled = (reason: string) => {
    showToast(reason, 'info');
  };

  const projectIdValid = projectId && projectId !== 'undefined' && projectId !== 'null';

  const baseWorkspaceForAgent =
    (projectIdValid && projectId) || workspaceIdForAgents || activeWorkspaceId || workspaces[0]?.id;

  const fallbackAgentId = (currentAgent && currentAgent.id) || agents[0]?.id;

  const conversationsPath =
    hasAgents && baseWorkspaceForAgent && fallbackAgentId
      ? `/workspaces/${baseWorkspaceForAgent}/agents/${fallbackAgentId}/conversations`
      : undefined;

  const actionsPath =
    hasAgents && baseWorkspaceForAgent && fallbackAgentId
      ? `/workspaces/${baseWorkspaceForAgent}/agents/${fallbackAgentId}/actions`
      : undefined;

  const voiceAgentPath =
    hasAgents && baseWorkspaceForAgent && fallbackAgentId
      ? `/workspaces/${baseWorkspaceForAgent}/agents/${fallbackAgentId}/voice-agent`
      : undefined;

  const automationsPath =
    hasAgents && baseWorkspaceForAgent && fallbackAgentId
      ? `/workspaces/${baseWorkspaceForAgent}/agents/${fallbackAgentId}/automations`
      : undefined;

  const channelsBasePath =
    hasAgents && baseWorkspaceForAgent && fallbackAgentId
      ? `/workspaces/${baseWorkspaceForAgent}/agents/${fallbackAgentId}/channels`
      : undefined;

  const settingsPath = projectId && projectId !== 'undefined' && projectId !== 'null'
    ? `/workspaces/${projectId}`
    : '/dashboard';

  // workspaceIdForNavigation is reserved for future use
  // const workspaceIdForNavigation =
  //   projectId && projectId !== 'undefined' && projectId !== 'null'
  //     ? projectId
  //     : activeWorkspaceId || workspaces[0]?.id;


  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return '?';
    if (user.name) {
      const parts = user.name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return user.name[0].toUpperCase();
    }
    return user.email[0].toUpperCase();
  };

  const handleLogout = async () => {
    try {
      await logout();
      showToast('Logged out successfully', 'success');
    } catch (err) {
      showToast('Failed to logout', 'error');
    }
  };

  const navItemBase =
    "w-full flex items-center gap-[10px] rounded-[10px] px-3 py-2 cursor-pointer transition-all [transition-duration:160ms] ease-out overflow-hidden text-ellipsis font-['Plus_Jakarta_Sans',_sans-serif] text-[14px] leading-6 tracking-[-0.084px]";
  const navItemActive = "bg-gradient-nav-active text-white font-bold shadow-nav-active";
  const navItemInactive = "bg-transparent text-[var(--text-primary)] font-semibold hover-bg-app-hover";
  const navItemDisabled = "opacity-40 cursor-not-allowed";

  const navItemClass = (isActive: boolean, isDisabled?: boolean) =>
    [navItemBase, isActive ? navItemActive : navItemInactive, isDisabled ? navItemDisabled : '']
      .filter(Boolean)
      .join(' ');

  // Active items sit on the gradient pill, so their icon stays flat white;
  // every other icon is painted by the shared violet→cyan gradient.
  const navIconClass = (isActive: boolean) =>
    `w-[22px] h-[22px] min-w-[22px] flex items-center justify-center text-[18px] leading-none shrink-0 ${isActive ? 'text-white' : 'icon-gradient'}`;

  const navLabelClass = 'overflow-hidden text-ellipsis whitespace-nowrap min-w-0';

  return (
    <div className="flex min-h-screen">
      <IconGradientDefs />

      {mobileNavOpen && (
        <div
          className="hidden max-[900px]:block fixed inset-0 bg-[rgba(15,15,20,0.45)] z-[200]"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      <aside
        className={`w-[260px] px-[18px] py-5 border-r border-[var(--sidebar-border)] bg-app-sidebar shadow-[8px_0_30px_rgba(111,106,248,0.05)] flex flex-col gap-5 transition-[background,border-color] duration-300 [transition-timing-function:ease] h-screen sticky top-0 overflow-hidden max-[900px]:fixed max-[900px]:left-0 max-[900px]:top-0 max-[900px]:w-[280px] max-[900px]:max-w-[82vw] max-[900px]:z-[210] max-[900px]:shadow-[8px_0_40px_rgba(0,0,0,0.18)] max-[900px]:transition-transform max-[900px]:[transition-duration:250ms] max-[900px]:[transition-timing-function:ease] ${mobileNavOpen ? 'max-[900px]:translate-x-0' : 'max-[900px]:-translate-x-full'
          }`}
      >
        <div className="flex items-center justify-center pt-1 pb-3 shrink-0 max-[900px]:justify-between">
          <img src="/Vina-logo.svg" alt="Vina AI" className="h-[50px] w-auto" />
          <button
            className="hidden max-[900px]:flex items-center justify-center w-8 h-8 rounded-lg border-none bg-transparent text-[var(--text-primary)] cursor-pointer shrink-0"
            onClick={() => setMobileNavOpen(false)}
            aria-label="Close menu"
          >
            <FiX size={20} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {/* Home holds only the dashboard, so the heading goes with it rather
              than standing over an empty section — same rule as Orchestrate and
              Monitor below. */}
          {canViewDashboard && (
          <div className="flex flex-col gap-1">
            <div className="overflow-hidden text-ellipsis text-[var(--text-muted)] font-['Plus_Jakarta_Sans',_sans-serif] text-[12px] font-bold leading-6 tracking-[-0.072px] uppercase mb-1">Home</div>
            <button
              className={navItemClass(isDashboard || activeSection === 'dashboard')}
              onClick={() => navigate('/dashboard')}
            >
              <span className={navIconClass(isDashboard || activeSection === 'dashboard')}><IconGrid /></span>
              <span className={navLabelClass}>Dashboard</span>
            </button>
          </div>
          )}

          {showOrchestrate && (
          <div className="flex flex-col gap-1">
            <div className="overflow-hidden text-ellipsis text-[var(--text-muted)] font-['Plus_Jakarta_Sans',_sans-serif] text-[12px] font-bold leading-6 tracking-[-0.072px] uppercase mb-1">Orchestrate</div>
            {canViewAgents && (
              <button
                className={navItemClass(
                  (activeSection === 'agents' || activeSection === 'knowledge') &&
                    !location.pathname.includes('/actions'),
                )}
                onClick={goToAgents}
              >
                <span
                  className={navIconClass(
                    (activeSection === 'agents' || activeSection === 'knowledge') &&
                      !location.pathname.includes('/actions'),
                  )}
                ><IconWand /></span>
                <span className={navLabelClass}>Agent (AI Builder)</span>
              </button>
            )}

            {canManageChannels && (
              <button
                className={navItemClass(activeSection === 'channels', !hasAgents)}
                onClick={() => {
                  if (!channelsBasePath || !hasAgents) {
                    explainDisabled('Channels configuration requires an agent in this workspace');
                    return;
                  }
                  navigate(channelsBasePath);
                }}
                disabled={!hasAgents}
                title={!hasAgents ? 'Create an agent in this workspace to configure channels' : ''}
              >
                <span className={navIconClass(activeSection === 'channels')}><IconWebChat /></span>
                <span className={navLabelClass}>Channels</span>
              </button>
            )}

            {canManageActions && (
              <button
                className={navItemClass(activeSection === 'agents' && location.pathname.includes('/actions'), !hasAgents)}
                onClick={() => {
                  if (!actionsPath || !hasAgents) {
                    explainDisabled('Actions configuration requires an agent in this workspace');
                    return;
                  }
                  navigate(actionsPath);
                }}
                disabled={!hasAgents}
                title={!hasAgents ? 'Create an agent in this workspace to configure actions' : ''}
              >
                <span className={navIconClass(activeSection === 'agents' && location.pathname.includes('/actions'))}><IconSparkle /></span>
                <span className={navLabelClass}>Actions</span>
              </button>
            )}

            {canUseVoiceAgent && (
              <button
                className={navItemClass(activeSection === 'voiceAgent', !hasAgents)}
                onClick={() => {
                  if (!voiceAgentPath || !hasAgents) {
                    explainDisabled('Voice Agent requires an agent in this workspace');
                    return;
                  }
                  navigate(voiceAgentPath);
                }}
                disabled={!hasAgents}
                title={!hasAgents ? 'Create an agent in this workspace to use the voice agent' : ''}
              >
                <span className={navIconClass(activeSection === 'voiceAgent')}><IconVoice /></span>
                <span className={navLabelClass}>Voice Agent</span>
              </button>
            )}

            {canUseAutomations && (
              <button
                className={navItemClass(location.pathname.includes('/automations'), !hasAgents)}
                onClick={() => {
                  if (!automationsPath || !hasAgents) {
                    explainDisabled('Automations require an agent in this workspace');
                    return;
                  }
                  navigate(automationsPath);
                }}
                disabled={!hasAgents}
                title={!hasAgents ? 'Create an agent first to schedule automations' : ''}
              >
                <span className={navIconClass(location.pathname.includes('/automations'))}><IconAutomation /></span>
                <span className={navLabelClass}>Automations</span>
              </button>
            )}

            {!hasAgents && canManageAgents && (
              <p className="text-[11px] text-[var(--text-muted)] mt-1.5 leading-[1.5]">
                Actions will unlock after you{' '}
                <button className="bg-transparent border-none p-0 m-0 text-[inherit] text-[#5B5BF5] underline cursor-pointer" onClick={goToAgents}>
                  create your first agent
                </button>
                .
              </p>
            )}
          </div>
          )}

          {showMonitor && (
          <div className="flex flex-col gap-1">
            <div className="overflow-hidden text-ellipsis text-[var(--text-muted)] font-['Plus_Jakarta_Sans',_sans-serif] text-[12px] font-bold leading-6 tracking-[-0.072px] uppercase mb-1">Monitor</div>

            {canViewConversations && (
              <button
                className={navItemClass(activeSection === 'conversations', !hasAgents)}
                onClick={() => {
                  if (!conversationsPath || !hasAgents) {
                    explainDisabled('Conversations require an agent in this workspace');
                    return;
                  }
                  navigate(conversationsPath);
                }}
                disabled={!hasAgents}
                title={!hasAgents ? 'Create an agent in this workspace to view conversations' : ''}
              >
                <span className={navIconClass(activeSection === 'conversations')}><IconConversations /></span>
                <span className={navLabelClass}>Conversations</span>
              </button>
            )}

            {canViewWorkspaceSettings && (
              <button
                className={navItemClass(activeSection === 'settings')}
                onClick={() => navigate(settingsPath)}
              >
                <span className={navIconClass(activeSection === 'settings')}><IconSettings /></span>
                <span className={navLabelClass}>Settings</span>
              </button>
            )}
          </div>
          )}

          {showAdministration && (
          <div className="flex flex-col gap-1">
            <div className="overflow-hidden text-ellipsis text-[var(--text-muted)] font-['Plus_Jakarta_Sans',_sans-serif] text-[12px] font-bold leading-6 tracking-[-0.072px] uppercase mb-1">Administration</div>

            {canViewUsers && (
              <button
                className={navItemClass(activeSection === 'users')}
                onClick={() => navigate('/users')}
              >
                <span className={navIconClass(activeSection === 'users')}><IconProfile /></span>
                <span className={navLabelClass}>Users</span>
              </button>
            )}

            {/* HIDDEN: Security nav item temporarily disabled
            <button
              className={`sidebar-nav-item ${activeSection === 'security' ? 'active' : ''}`}
              onClick={() => navigate('/security')}
            >
              <span className="sidebar-nav-icon"><IconSecurity /></span>
              <span>Security</span>
            </button>
            */}

            {/* HIDDEN: Billing nav item temporarily disabled
            <button
              className={`sidebar-nav-item ${activeSection === 'billing' ? 'active' : ''}`}
              onClick={() => navigate('/billing')}
            >
              <span className="sidebar-nav-icon"><IconBilling /></span>
              <span>Billing</span>
            </button>
            */}

            {/* HIDDEN: Tools nav item temporarily disabled
            <button
              className={`sidebar-nav-item ${activeSection === 'tools' ? 'active' : ''}`}
              onClick={() => navigate('/tools')}
            >
              <span className="sidebar-nav-icon"><IconTools /></span>
              <span>Tools</span>
            </button>
            */}

            {canViewEscalations && (
              <button
                className={navItemClass(activeSection === 'escalations')}
                onClick={() => navigate('/escalations')}
              >
                <span className={navIconClass(activeSection === 'escalations')}><IconTicket /></span>
                <span className={navLabelClass}>Support Tickets</span>
              </button>
            )}

            {canViewLeads && (
              <button
                className={navItemClass(activeSection === 'leads')}
                onClick={() => navigate('/leads')}
              >
                <span className={navIconClass(activeSection === 'leads')}><FiTarget /></span>
                <span className={navLabelClass}>Leads</span>
              </button>
            )}

            {canViewLogs && (
              <button
                className={navItemClass(activeSection === 'logs')}
                onClick={() => navigate('/logs')}
              >
                <span className={navIconClass(activeSection === 'logs')}><IconLogs /></span>
                <span className={navLabelClass}>Logs</span>
              </button>
            )}
          </div>
          )}
        </nav>

        {/* User Menu */}
        {user && (
          <div className="relative mt-auto shrink-0 pt-4 border-t border-[var(--border-color)]" ref={userMenuRef}>
            <button
              className="w-full px-3 py-[10px] rounded-[14px] border-none bg-[var(--bg-subtle)] text-[var(--text-primary)] flex items-center gap-2 cursor-pointer transition-all duration-150 ease-in-out font-['Plus_Jakarta_Sans',_sans-serif] hover-bg-app-hover"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="w-9 h-9 rounded-full bg-gradient-brand-accent flex items-center justify-center text-white text-[13px] font-semibold shrink-0">
                {getUserInitials()}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="font-['Plus_Jakarta_Sans',_sans-serif] text-[13px] font-bold text-[var(--text-primary)] overflow-hidden text-ellipsis whitespace-nowrap">{user.name || 'User'}</div>
                <div className="font-['Plus_Jakarta_Sans',_sans-serif] text-[11px] font-medium text-[var(--text-muted)] overflow-hidden text-ellipsis whitespace-nowrap">{user.email}</div>
              </div>
              <span className={`flex items-center text-[var(--text-muted)] transition-transform duration-150 ease-in-out shrink-0 ${showUserMenu ? 'rotate-90' : ''}`}>
                <FiChevronRight size={16} />
              </span>
            </button>

            {showUserMenu && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-[var(--bg-card-flat)] border border-[var(--border-color)] rounded-xl shadow-[0_8px_24px_rgba(111,106,248,0.15)] overflow-hidden z-[1000]">
                <button
                  className="w-full py-2 px-3 border-none bg-transparent text-[var(--text-primary)] font-['Plus_Jakarta_Sans',_sans-serif] text-[13px] text-left cursor-pointer flex items-center gap-2 transition-[background] duration-150 ease-in-out hover-bg-app-hover"
                  onClick={() => { navigate('/profile'); setShowUserMenu(false); }}
                >
                  <span style={{ display: 'flex', alignItems: 'center' }}><IconProfile size={16} /></span>
                  <span>Profile</span>
                </button>
                <button
                  className="w-full py-2 px-3 border-none border-t border-t-[var(--border-color)] bg-transparent text-[var(--text-primary)] font-['Plus_Jakarta_Sans',_sans-serif] text-[13px] text-left cursor-pointer flex items-center gap-2 transition-[background] duration-150 ease-in-out hover-bg-app-hover"
                  onClick={handleLogout}
                >
                  <span style={{ display: 'flex', alignItems: 'center' }}><IconLogout size={16} /></span>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        )}
      </aside>

      <main className="flex-1 min-w-0 bg-app-primary max-[900px]:w-full">
        <header className="sticky top-0 z-40 flex items-center justify-between gap-4 flex-wrap py-5 px-7 bg-app-primary border-b border-[var(--border-color)] max-[900px]:px-4 max-[900px]:py-4 max-[560px]:flex-col max-[560px]:items-start">
          <div className="flex items-center gap-3 min-w-0 max-[560px]:w-full">
            <button
              className="hidden max-[900px]:flex items-center justify-center w-9 h-9 rounded-[10px] border-none bg-transparent text-[var(--text-primary)] cursor-pointer shrink-0"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open menu"
            >
              <FiMenu size={22} />
            </button>

            <div className="min-w-0">
              {pageTitle && <h1 className="overflow-hidden text-ellipsis text-[var(--text-primary)] font-['Plus_Jakarta_Sans',_sans-serif] text-[20px] font-bold leading-[normal] m-0">{pageTitle}</h1>}
              {pageDescription && <p className="text-[var(--text-primary)] opacity-80 font-['Plus_Jakarta_Sans',_sans-serif] text-[14px] font-normal leading-[normal] mt-1">{pageDescription}</p>}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap max-[560px]:w-full">
            {headerActions}
            <div className="inline-flex items-center justify-center box-border h-11 px-[14px] rounded-[10px] border border-[var(--border-color)] bg-transparent">
              <SkyToggle />
            </div>
          </div>
        </header>

        {/* CUSTOMER only: free-trial message allowance and expiry. */}
        <TrialLimitBanner />

        {/* Keep existing container/header styles inside pages */}
        {children}
      </main>
    </div>
  );
};

export default ProjectSidebarLayout;

